// Node Modules
import moment from 'moment';
import { nanoid } from 'nanoid';
import { RichText } from 'prismic-reactjs';
import { useState } from 'react';

// Components
import TopBanner from '@/components/global-components/top-banner/TopBanner';
import StyledCard from '@/components/global-components/styled-card/StyledCard';
import Button from '@/components/global-components/Button';
import SearchSidebar from '@/components/global-components/search-sidebar/SearchSidebar';
import Pagination from '@/components/global-components/pagination/Pagination';
import Container from '@/components/global-components/Container';

// Scripts
import { fetchPosts } from '@/services/posts'

// Themes
import theme from '@/themes/index';

const BlogBody = ({
  content,
  isMobile,
  isTablet,
  isDesktop,
}) => {
  const [postsState, setPostsState] = useState(content.posts);
  const [postsInfoState, setPostsInfoState] = useState(content.postsInfo);
  const [activePage, setActivePage] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(Math.ceil(content.postsInfo.totalCount / content.posts.length));

  const contentPerPage =  content.posts.length;

  let style = {
    height: '27.75rem',
    padding: '8rem 1rem 4rem',
  };
  if (isTablet) {
    style = {
      height: '27.75rem',
      paddingTop: '4.75rem',
    };
  } else if (!isMobile) {
    style = {
      height: '33.75rem',
      paddingTop: '8.75rem',
    };
  }

  const updatePosts = response => {
    if (response?.data) {
      setPostsState(response?.data?.posts);
      setPostsInfoState(response?.data?.postsInfo);
      setNumberOfPages(Math.ceil(response?.data?.postsInfo.totalCount / content.posts.length));
    }
  }

  const searchTerm = async term => {
    if(term) {
      await getPosts(contentPerPage, null, term, '', '');
    } else {
      setPostsState(content.posts);
      setNumberOfPages(Math.ceil(content.postsInfo.totalCount / content.posts.length));
      setPostsInfoState(content.postsInfo);
    }
    setActivePage(1);
  }

  const getPosts = async (first, last, searchText, beforeCursor, afterCursor) => {
    return fetchPosts(first, last, searchText, beforeCursor, afterCursor)
      .catch(function (error) {
        console.log('ERROR: ', error);
      }).then(response => {
        updatePosts(response, first);
        return response;
      });
  }

  const handleChange = async (cursor, action) => {
    if (action === 'next') {
      await getPosts(contentPerPage, null, '', '', cursor);
      setActivePage(activePage+1);
    } else if(action === 'previous') {
      await getPosts(null, contentPerPage, '', cursor, '');
      setActivePage(activePage-1);
    }
  };

  const getPostContent = post => {
    return (
      <>
        <div className='post-container'>
          <h5>{post.title}</h5>
          <span className='details'>{post.location} / {moment(post.date).format('D MMMM YYYY')}</span>
          <span className='reading'>{post.timeReading}</span>
          <RichText render={post.summary} />
          <Button
            text={post.actionButton.title}
            className={`custom ${post.actionButton.style.toLowerCase()}`}
            href={`/post/${post.meta.id}`}
            target='_self'
          />
        </div>
        <style jsx>{`
          div.post-container {
            display: flex;
            flex-direction: column;
          }

          div.post-container h5 {
            margin-block-start: 0;
            margin-block-end: 0;
          }

          div.post-container span.details {
            padding-top: 0.5rem;
            font-size: 1rem;
          }

          div.post-container span.reading {
            color: ${theme.fontColors.optional3};
            padding-bottom: 1.5rem;
            line-height: 1.5rem;
          }

          div.post-container .custom {
            margin-top: 2rem;
          }

          @media only screen and (min-width: ${theme.breakpoints.desktopMin}) {
            div.post-container span.details {
              font-size: 1.125rem;
              line-height: 1.5rem;
            }
          }
        `}</style>
        <style global jsx>{`
          div.post-container .custom {
            margin-top: 2rem;
          }

          div.post-container p {
            margin-block-start: 0;
            margin-block-end: 0;
          }
        `}</style>
      </>
    );
  };

  return (
    <>
      <section className='top-banner'>
        <TopBanner 
          {...content.topBanner}
          style={{
            ...style,
          }} 
        />
      </section>
      <section className='posts'>
        <Container style={isDesktop ? {
          display: 'flex', 
        } : {
          display: 'flex', flexDirection: 'column-reverse', 
        }}>
          <div className='posts-container'>
            {!postsState || !postsState.length && <h6>No Posts found!</h6>}
            {postsState.map(post => (
              <StyledCard 
                key={nanoid()}
                content={{
                  image: post.node.previewImage,
                }}
                containerStyle={{
                  flexDirection: isDesktop ? 'row-reverse' : 'column-reverse', 
                }}
                infoContainerStyle={{
                  padding: '0',
                  paddingLeft: isDesktop && '4.063rem',
                  textAlign: !isDesktop && 'left',
                }}
                imageStyles={{
                  minWidth: isDesktop ? '25.75rem' : '100%',
                  maxHeight: isDesktop ? '25rem' : '100%',
                }}
              >
                {getPostContent(post.node)}
              </StyledCard>
            ))}
            <Pagination pageInfo={postsInfoState.pageInfo} numberOfPages={numberOfPages} pageChange={handleChange} activePage={activePage} />
          </div>
          <div className='recent-posts-container'>
            <SearchSidebar recentPosts={content.posts.slice(0, 4)} getSearchTerm={searchTerm} />
          </div>
        </Container>
      </section>
      <style jsx>{`
        section.posts {
          padding: 7.5rem 0.625rem 5rem;
          display: flex;
          flex-direction: column-reverse;
        }

        section.posts .posts-container {
          display: flex;
          flex-direction: column;
          gap: 3rem;
        }

        section.posts .recent-posts-container {
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: 20.5rem;
          padding-bottom: 5rem;
        }

        @media only screen and (min-width: ${theme.breakpoints.tabletMin}) {
          section.posts .posts-container {
            gap: 5rem;
          }

          section.posts .recent-posts-container {
            max-width: 100%;
          }
        }

        @media only screen and (min-width: ${theme.breakpoints.desktopMin}) {
          section.posts {
            flex-direction: row;
          }

          section.posts .posts-container {
            padding-right: 4.9375rem;
            gap: 3rem;
            min-width: 63.1875rem;
          }

          section.posts .recent-posts-container {
            padding-bottom: 0;
            max-width: 20.5rem;
          }
        }
      `}</style>
    </>
  );
};

export default BlogBody;
